/* eslint-disable react/no-unstable-nested-components */
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { windowWrapper } from '@clubmed/utils';
import { classnames } from 'Utils/classnames/classnames.utils';
import { useFormikContext } from 'formik';
import { FormikInput } from 'Components/frameworks/formik/FormikInput';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ICONS } from 'Components/utils/icon/icons';
import { ServicesContext } from 'Context/ServicesContext';
import { t } from 'Src/i18n';
import { CHILDCARE_STOCK_THRESHOLD } from 'Utils/childcare/childcare.consts';
import { DateSchedule } from 'Components/widgets/reservation-block/DateSchedule';
import { ScheduleEnvelopeFSS } from 'Components/widgets/reservation-block/ScheduleEnvelopeFSS';
import {
  isDayInHolidayInnerRange,
  isHolidayFullStaySchedule,
  isHolidayInnerRangeSchedule,
  isMultiDaySchedule,
  isSingleDaySchedule,
} from 'Components/widgets/reservation-block/reservationBlock.utils';

type Props = {
  stayDepartureDate: string;
  stayArrivalDate: string;
  attendeeId: string;
};

type Schedule = {
  startDate: string;
  endDate: string;
  isSelected?: boolean;
  isPaid?: boolean;
  isFullyPaid?: boolean;
  remainingStock?: number;
  timeSlots?: Array<{ isPaid?: boolean } | string>;
};

const deferFieldUpdate = (callback: () => void) => {
  windowWrapper.requestAnimationFrame(callback);
};

export const ReservationScheduleStepFSS = ({
  stayDepartureDate: departureDate,
  stayArrivalDate: arrivalDate,
  attendeeId,
}: Props) => {
  const { scheduleReservationMessage } = useContext(ServicesContext);
  const { values, validateForm, setFieldValue } = useFormikContext();
  const [lastAvailableSchedule, setLastAvailableSchedule] = useState<{
    isVisible?: boolean;
    nb?: number;
  }>({});
  const filteredAttendeesValues = [values.attendees[attendeeId]];

  useEffect(() => {
    validateForm();
    filteredAttendeesValues
      .filter(({ selected }) => !selected)
      .forEach(({ schedules: attendeeSchedules }) => {
        attendeeSchedules.forEach(({ isSelected }, scheduleIndex) => {
          if (!isSelected) {
            return;
          }

          setFieldValue(`attendees.${attendeeId}.schedules[${scheduleIndex}].isSelected`, false);
        });
      });
    return () => {
      validateForm();
    };
  }, []);

  return (
    <Fragment>
      {scheduleReservationMessage && (
        <p className="mt-10 text-12 font-bold text-sienna">{scheduleReservationMessage}</p>
      )}

      {filteredAttendeesValues.map(({ schedules }) => {
        const bundledScheduleIndex = schedules.findIndex((schedule) =>
          isMultiDaySchedule(schedule),
        );
        const bundledSchedule =
          bundledScheduleIndex >= 0 ? schedules[bundledScheduleIndex] : undefined;
        const isBundledFullStay =
          !!bundledSchedule &&
          isHolidayFullStaySchedule(bundledSchedule, arrivalDate, departureDate);
        const isBundledInnerRange =
          !!bundledSchedule &&
          isHolidayInnerRangeSchedule(bundledSchedule, arrivalDate, departureDate);
        const attendeePaidSchedules = schedules.filter(({ isPaid }) => isPaid);

        const setScheduleSelected = (scheduleIndex: number, isSelected: boolean) => {
          deferFieldUpdate(() => {
            setFieldValue(
              `attendees.${attendeeId}.schedules[${scheduleIndex}].isSelected`,
              isSelected,
            );
          });
        };

        const handleScheduleSelection = (
          scheduleIndex: number,
          schedule: Schedule,
          isChecking: boolean,
        ) => {
          if (!isChecking || bundledScheduleIndex < 0) {
            return;
          }

          const isBundledSelection = scheduleIndex === bundledScheduleIndex;
          const isInnerRangeDay = isDayInHolidayInnerRange(schedule, arrivalDate, departureDate);

          if (isBundledSelection) {
            setScheduleSelected(bundledScheduleIndex, true);

            schedules.forEach((currentSchedule, currentScheduleIndex) => {
              if (currentScheduleIndex === bundledScheduleIndex) {
                return;
              }

              if (!isSingleDaySchedule(currentSchedule)) {
                return;
              }

              if (isBundledFullStay) {
                setScheduleSelected(currentScheduleIndex, false);
                return;
              }

              if (
                isBundledInnerRange &&
                isDayInHolidayInnerRange(currentSchedule, arrivalDate, departureDate)
              ) {
                setScheduleSelected(currentScheduleIndex, false);
              }
            });
            return;
          }

          if (isBundledFullStay) {
            setScheduleSelected(bundledScheduleIndex, false);
            return;
          }

          if (isBundledInnerRange && isInnerRangeDay) {
            setScheduleSelected(bundledScheduleIndex, false);
          }
        };

        const handleAutoSwitchToBundle = (scheduleIndex: number) => {
          if (bundledScheduleIndex < 0 || attendeePaidSchedules.length > 0) {
            return;
          }

          const attendeeSchedules = values.attendees[attendeeId].schedules;
          const selectedSingleSchedules = schedules.filter(
            (currentSchedule, currentScheduleIndex) =>
              isSingleDaySchedule(currentSchedule) &&
              (currentScheduleIndex === scheduleIndex ||
                attendeeSchedules[currentScheduleIndex]?.isSelected),
          );

          if (isBundledFullStay) {
            const allSingleDaySchedules = schedules.filter((currentSchedule) =>
              isSingleDaySchedule(currentSchedule),
            );

            if (selectedSingleSchedules.length !== allSingleDaySchedules.length) {
              return;
            }

            schedules.forEach((currentSchedule, currentScheduleIndex) => {
              if (currentScheduleIndex === bundledScheduleIndex) {
                setScheduleSelected(currentScheduleIndex, true);
                return;
              }

              setScheduleSelected(currentScheduleIndex, false);
            });
            return;
          }

          if (isBundledInnerRange) {
            const innerRangeSingleSchedules = schedules.filter((currentSchedule) =>
              isDayInHolidayInnerRange(currentSchedule, arrivalDate, departureDate),
            );
            const selectedInnerRangeSingleSchedules = selectedSingleSchedules.filter(
              (currentSchedule) =>
                isDayInHolidayInnerRange(currentSchedule, arrivalDate, departureDate),
            );

            if (selectedInnerRangeSingleSchedules.length !== innerRangeSingleSchedules.length) {
              return;
            }

            schedules.forEach((currentSchedule, currentScheduleIndex) => {
              if (currentScheduleIndex === bundledScheduleIndex) {
                setScheduleSelected(currentScheduleIndex, true);
                return;
              }

              if (isDayInHolidayInnerRange(currentSchedule, arrivalDate, departureDate)) {
                setScheduleSelected(currentScheduleIndex, false);
              }
            });
          }
        };

        return (
          <div className="divide-y divide-lightGrey" key={attendeeId}>
            <div className="flex flex-wrap gap-10 py-10">
              {schedules.map((schedule, scheduleIndex) => {
                const { endDate, startDate } = schedule;
                const currentChildSchedule = values.attendees[attendeeId].schedules[scheduleIndex];
                const bundledScheduleIsPaid =
                  bundledSchedule?.isPaid || bundledSchedule?.timeSlots?.[0]?.isPaid || false;
                const isCurrentSingleAndBundledPaid =
                  isSingleDaySchedule(currentChildSchedule) && bundledScheduleIsPaid;
                const isCurrentBundledAndOneSinglePaid =
                  isMultiDaySchedule(currentChildSchedule) &&
                  schedules.some(
                    (currentSchedule) =>
                      isSingleDaySchedule(currentSchedule) && currentSchedule.isPaid,
                  );
                const isCurrentSchedulePaid =
                  !!currentChildSchedule?.isPaid || !!currentChildSchedule?.isFullyPaid;
                const isDisabled =
                  isCurrentSchedulePaid ||
                  isCurrentSingleAndBundledPaid ||
                  isCurrentBundledAndOneSinglePaid;
                const isBundledSchedule = scheduleIndex === bundledScheduleIndex;

                const PlaceHolderFSS = (isChecked: boolean) => (
                  <div
                    className={classnames(
                      'flex items-center rounded-12 border-1 px-10 py-5 text-10 font-bold',
                      {
                        'border-lightGrey text-black': !isChecked,
                        'border-saffron-active bg-saffron text-black': isChecked,
                        'italic opacity-50': isDisabled,
                        'w-1/2 justify-between': startDate !== endDate,
                      },
                    )}
                    data-testid="reservationScheduleDay"
                  >
                    <DateSchedule date={startDate} />
                    {startDate !== endDate && (
                      <Fragment>
                        <div className="mx-10">{t('common.to')}</div>
                        <DateSchedule date={endDate} />
                      </Fragment>
                    )}
                  </div>
                );

                return (
                  <ScheduleEnvelopeFSS
                    key={`${startDate}-${endDate}-Container`}
                    isBundledSchedule={isBundledSchedule}
                    isFullStayBundle={isBundledFullStay}
                    InputComp={() => (
                      <FormikInput
                        isDisabled={isDisabled}
                        key={`${startDate}${endDate}`}
                        dataTestId="scheduleCheckboxItem"
                        name={`attendees.${attendeeId}.schedules[${scheduleIndex}].isSelected`}
                        type="checkbox"
                        onChange={(e) => {
                          handleScheduleSelection(scheduleIndex, schedule, e.target.checked);

                          if (e.target.checked) {
                            handleAutoSwitchToBundle(scheduleIndex);
                          }

                          if (
                            !values.attendees[attendeeId].schedules[scheduleIndex].isSelected &&
                            values.attendees[attendeeId].schedules[scheduleIndex].remainingStock <=
                              CHILDCARE_STOCK_THRESHOLD
                          ) {
                            setLastAvailableSchedule({
                              isVisible: true,
                              nb: values.attendees[attendeeId].schedules[scheduleIndex]
                                .remainingStock,
                            });
                          } else {
                            setLastAvailableSchedule({});
                          }
                        }}
                        placeholderComponent={PlaceHolderFSS}
                      />
                    )}
                  />
                );
              })}
            </div>
          </div>
        );
      })}

      {lastAvailableSchedule.isVisible && (
        <p className="mt-5 flex items-center gap-5 text-14 font-bold leading-1 text-red">
          <Icon className="shrink-0" name={ICONS.WARNING} width="18px" />
          {t('booking.activities.remainingService', {
            nb: lastAvailableSchedule.nb,
          })}
        </p>
      )}
    </Fragment>
  );
};
